import React, { useState, useEffect } from 'react';
import { db } from '../config/firebase';
import { ref, get, update, query, orderByChild, equalTo } from 'firebase/database';
import { trackJobCompletion } from '../services/userBehaviorService';
import { sendJobCompletionNotification } from '../services/notificationService';
import '../styles/jobCompletionAdmin.css';

const JobCompletionAdmin = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending, completed
  const [selectedApp, setSelectedApp] = useState(null);
  const [completionNotes, setCompletionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchApplications();
  }, [filter]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const applicationsRef = ref(db, 'applications');
      const snapshot = await get(applicationsRef);
      
      if (snapshot.exists()) {
        const allApps = [];
        snapshot.forEach((jobSnapshot) => {
          jobSnapshot.forEach((appSnapshot) => {
            const app = appSnapshot.val();
            if (filter === 'pending' && app.status === 'accepted' && !app.completed) {
              allApps.push({ ...app, applicationId: appSnapshot.key, jobId: jobSnapshot.key });
            } else if (filter === 'completed' && app.completed) {
              allApps.push({ ...app, applicationId: appSnapshot.key, jobId: jobSnapshot.key });
            }
          });
        });
        setApplications(allApps.sort((a, b) => new Date(b.appliedAt) - new Date(a.appliedAt)));
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setMessage('Error loading applications');
      setLoading(false);
    }
  };

  const handleCompleteJob = async (app) => {
    if (!completionNotes.trim()) {
      setMessage('Please add completion notes');
      return;
    }

    try {
      setSubmitting(true);

      // Update application status
      const appRef = ref(db, `applications/${app.jobId}/${app.applicationId}`);
      await update(appRef, {
        completed: true,
        completedAt: new Date().toISOString(),
        completionNotes,
        status: 'completed'
      });

      // Track job completion for recommendations
      await trackJobCompletion(app.userId, app.jobId, {
        id: app.jobId,
        title: app.jobTitle || 'Job',
        skills: app.skills || [],
        type: app.type || 'Fulltime',
        location: app.location || '',
        experience: app.experience || '',
        matchScore: app.matchScore || 75
      });

      // Send notification to user
      await sendJobCompletionNotification(app.userId, {
        jobTitle: app.jobTitle || 'Job',
        completionNotes
      });

      setMessage('Job marked as completed! User has been notified');
      setCompletionNotes('');
      setSelectedApp(null);
      
      // Refresh list
      setTimeout(() => {
        fetchApplications();
        setMessage('');
      }, 2000);
    } catch (error) {
      console.error('Error completing job:', error);
      setMessage('Error marking job as complete');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="job-completion-admin">
      <div className="completion-header">
        <h1>
          <i className="bx bxs-check-circle"></i> Job Completion Management
        </h1>
        <p>Review and confirm completed jobs to unlock recommendations</p>
      </div>

      <div className="completion-filters">
        <button 
          className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          <i className="bx bxs-time"></i> Pending Review ({applications.length})
        </button>
        <button 
          className={`filter-btn ${filter === 'completed' ? 'active' : ''}`}
          onClick={() => setFilter('completed')}
        >
          <i className="bx bxs-check-double"></i> Completed ({applications.length})
        </button>
      </div>

      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          <i className={`bx ${message.includes('Error') ? 'bxs-x-circle' : 'bxs-check-circle'}`}></i>
          {message}
        </div>
      )}

      {loading ? (
        <div className="loading">
          <i className="bx bx-loader-alt"></i> Loading applications...
        </div>
      ) : applications.length === 0 ? (
        <div className="empty-state">
          <i className="bx bxs-inbox"></i>
          <p>No {filter} applications</p>
        </div>
      ) : (
        <div className="applications-list">
          {applications.map((app) => (
            <div key={app.applicationId} className="app-card">
              <div className="app-header">
                <div className="app-info">
                  <h3>{app.jobTitle}</h3>
                  <p className="user-name">{app.fullName}</p>
                </div>
                <div className="app-meta">
                  <span className="badge applied">
                    <i className="bx bxs-calendar"></i>
                    {new Date(app.appliedAt).toLocaleDateString()}
                  </span>
                  {app.completed && (
                    <span className="badge completed">
                      <i className="bx bxs-check-circle"></i>
                      Completed
                    </span>
                  )}
                </div>
              </div>

              <div className="app-details">
                <div className="detail-row">
                  <span className="detail-label">
                    <i className="bx bxs-envelope"></i> Email
                  </span>
                  <span className="detail-value">{app.email}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">
                    <i className="bx bxs-phone"></i> Phone
                  </span>
                  <span className="detail-value">{app.phone}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">
                    <i className="bx bxs-map"></i> Location
                  </span>
                  <span className="detail-value">{app.location}</span>
                </div>
                {app.experience && (
                  <div className="detail-row">
                    <span className="detail-label">
                      <i className="bx bxs-briefcase"></i> Experience
                    </span>
                    <span className="detail-value">{app.experience}</span>
                  </div>
                )}
              </div>

              {filter === 'pending' && (
                <div className="completion-form">
                  <textarea
                    placeholder="Add completion notes (required)"
                    value={selectedApp?.applicationId === app.applicationId ? completionNotes : ''}
                    onChange={(e) => {
                      setSelectedApp(app);
                      setCompletionNotes(e.target.value);
                    }}
                    onFocus={() => setSelectedApp(app)}
                    rows="3"
                  ></textarea>
                  <button
                    className="btn-complete"
                    onClick={() => handleCompleteJob(app)}
                    disabled={submitting || (selectedApp?.applicationId === app.applicationId && !completionNotes.trim())}
                  >
                    <i className="bx bxs-check-circle"></i>
                    {submitting ? 'Marking Complete...' : 'Mark as Completed'}
                  </button>
                </div>
              )}

              {filter === 'completed' && app.completionNotes && (
                <div className="completion-notes">
                  <strong>Completion Notes:</strong>
                  <p>{app.completionNotes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default JobCompletionAdmin;
